<template>
  <div>
    <div class="flex flex-wrap">
      <div class="w-full md:pl-10 md:w-3/4 md:pr-20">
        <form method="POST">

          <div class="mb-12">
            <label for="title">
              Event Title <span class="text-red">*</span>
              <span class="text-sm font-sans-book block">
                60 character maximum (including spaces).
              </span>
            </label>

            <input id="title" v-model="form.title" v-validate="titleValidate" name="title" type="text">

            <div class="mt-1 text-sm font-sans-italic text-gray-dark">
              Character count: {{ characterCount }}
            </div>

            <span v-show="errors.has('title')" class="text-lg error text-error-red">
              {{ errors.first('title') }}
            </span>
          </div>

          <div class="mb-12">
            <label for="hasHostedBefore">
              Have you hosted a Reimagine event before? <span class="text-red">*</span>
            </label>
            <RadioGroup
              name="hasHostedBefore"
              id="hasHostedBefore"
              v-model="form.hasHostedBefore"
              v-validate="'required'"
              :options="[{title: 'No', value: false}, {title: 'Yes', value: true}]"
            />
            <span v-show="errors.has('hasHostedBefore')" class="text-lg error text-error-red">{{
                errors.first('hasHostedBefore')
              }}</span>
          </div>

          <div class="mb-12">
            <label for="referredBy">
              If you were referred by a Reimagine staff member, please enter their name.
            </label>
            <input id="referredBy" v-model="form.referredBy" name="referredBy" type="text" />
            <span v-show="errors.has('referredBy')" class="text-lg error text-error-red">
              {{errors.first('referredBy')}}
            </span>
          </div>

          <div class="mb-12">
            <label for="openToPartnership">
              To make our events more inclusive, we often collaborate with leaders of community-based organizations and match them with Reimagine collaborators whose offerings would resonate with their communities. Are you interested in partnerships with these organizations? <span class="text-red">*</span>
            </label>
            <RadioGroup
              name="openToPartnership"
              id="openToPartnership"
              v-model="form.openToPartnership"
              v-validate="'required'"
              :options="[{title: 'No', value: false}, {title: 'Yes', value: true}]"
            />
            <span v-show="errors.has('openToPartnership')" class="text-lg error text-error-red">
              {{errors.first('openToPartnership') }}
            </span>
          </div>

          <div class="mb-12">
            <label for="needMiniGrant">
              Are you interested in applying for a mini-grant or additional marketing support for your event? <span class="text-red">*</span>
            </label>
            <RadioGroup
              name="needMiniGrant"
              id="needMiniGrant"
              v-model="form.needMiniGrant"
              v-validate="'required'"
              :options="[{title: 'No', value: false}, {title: 'Yes', value: true}]"
            />
            <span v-show="errors.has('needMiniGrant')" class="text-lg error text-error-red">
              {{errors.first('needMiniGrant') }}
            </span>
          </div>

          <div class="mb-12">
            <h2 class="font-sans text-n-gray-dark">Event Calendar:</h2>
            <div class="font-sans text-n-gray-dark text-32">{{ festivalTitle }}</div>
          </div>

          <div class="mb-12">
            <label for="startDate">
              Day <span class="text-red">*</span>
            </label>

            <lab-date-picker
              id="startDate"
              name="startDate"
              v-validate="'required'"
              :days="days"
              v-model="form.startDate"
              :start-at="festivalStartAt"
              :end-at="festivalEndAt"
            />

            <span v-show="errors.has('startDate')" class="text-lg error text-error-red">
              {{errors.first('startDate') }}
            </span>
          </div>

          <div class="mb-12">
            <label for="loadStartTime">
              Start Time <span class="text-red">*</span>

              <span class="text-sm font-sans-book block">
                Please enter all times in Pacific Daylight Time (PDT). If you are unsure of the time zone conversion, you can easily calculate it
                <a href="https://www.timeanddate.com/worldclock/converter.html?iso=20200330T180000&p1=224" target="_blank">
                  here
                </a>.
              </span>
            </label>

            <time-picker
              v-model="form.loadStartTime"
              v-validate="'required|time_before:loadEndTime'"
              ref="loadStartTime"
              id="loadStartTime"
              name="loadStartTime"
            />

            <span v-show="errors.has('loadStartTime')" class="text-lg error text-error-red">
              {{ errors.first('loadStartTime') }}
            </span>
          </div>

          <div class="mb-12">
            <label for="loadEndTime">
              End Time <span class="text-red">*</span>
            </label>

            <time-picker
              v-model="form.loadEndTime"
              v-validate="'required|time_after:loadStartTime'"
              ref="loadEndTime"
              id="loadEndTime"
              name="loadEndTime"
            />

            <span v-show="errors.has('loadEndTime')" class="text-lg error text-error-red">
              {{errors.first('loadEndTime')}}
            </span>
          </div>

          <div class="mb-12">
            <label for="title">
              Access Preference? <span class="text-red">*</span>
              <div class="text-sm font-sans-book">
                Do you want to display this event in the public event calendar schedule or
                make it private so only those with a direct link can access?
              </div>
            </label>
            <div class="group__radio">
              <div class="flex flex-wrap select-box">
                <label class="w-full text-base font-normal md:w-1/2 md:pr-3">
                  <input v-model="form.private" name="private"
                         type="radio"
                         value="false"
                  >
                  <span class="inline-block mr-2 radio"></span> Public
                </label>

                <label class="w-full text-base font-normal md:w-1/2 md:pr-3">
                  <input v-model="form.private" v-validate="'required'"
                         name="private"
                         type="radio"
                         value="true"
                  >
                  <span class="inline-block mr-2 radio"></span> Private
                </label>
              </div>
              <span v-show="errors.has('type')" class="text-lg error text-error-red">{{
                  errors.first('private')
                }}</span>
            </div>
          </div>

          <honor-of-manager ref="honorOfManagerRef" v-bind="{
            isInHonorOf: form.isInHonorOf,
            inHonorOfName: form.inHonorOfName,
            inHonorOfPhoto: form.inHonorOfPhoto,
            inHonorOfDescription: form.inHonorOfDescription,
            isInHonorOfChange:  v => form.isInHonorOf = v,
            inHonorOfNameChange: v => form.inHonorOfName = v,
            inHonorOfPhotoChange:  v => form.inHonorOfPhoto = v,
            inHonorOfDescriptionChange: v => form.inHonorOfDescription = v,
            isValidChange: v => honorOfValid = v
          }" />

          <div class="mb-12">
            <label for="title">
              What type of event is this? <span class="text-red">*</span>
            </label>
            <div class="group__radio">
              <div class="flex flex-wrap select-box">
                <label class="w-full text-base font-normal md:w-1/2 md:pr-3">
                  <input v-model="form.type" name="type"
                         type="radio"
                         value="digital"
                  >
                  <span class="inline-block mr-2 radio"></span> Virtual
                </label>

                <label class="w-full text-base font-normal md:w-1/2 md:pr-3">
                  <input v-model="form.type" v-validate="'required'"
                         name="type"
                         type="radio"
                         value="physical"
                  >
                  <span class="inline-block mr-2 radio"></span> In-person
                </label>
              </div>
              <span v-show="errors.has('type')" class="text-lg error text-error-red">{{ errors.first('type') }}</span>
            </div>
          </div>

          <div class="mb-12">
            <label for="acceptDonations">Accept Donations</label>
            <div class="select-box">
              <label class="text-base font-normal" for="acceptDonations">
                <input type="checkbox" id="acceptDonations" v-model="form.acceptDonations">
                <span class="check inline-block mr-2"></span>Yes
              </label>
            </div>
          </div>

          <div v-if="form.type=='digital'" key="digital-event">
            <div class="mb-12">
              <label for="venueName">
                Video Streaming Service
                <div class="text-sm font-sans-book">Zoom, Facebook Live, Instagram Live, YouTube, etc.</div>
              </label>
              <input
                id="venueName" key="digital-venue-name" v-model="form.venueName"
                name="venueName"
                type="text"
              >
            </div>

            <div class="mb-12">
              <label for="venueUrl">
                Webinar or Livestream URL <span class="text-red">*</span>
                <div class="text-sm font-sans-book">
                  Please test all links to make sure they are functional.
                </div>
              </label>

              <url-input v-model="form.venueUrl" v-validate="'required'" :required="true" name="venueUrl"
                         placeholder="www.zoom.us"></url-input>
              <span v-show="errors.has('venueUrl')" class="text-lg error text-error-red">{{
                  errors.first('venueUrl')
                }}</span>
            </div>

            <div class="mb-12">
              <label for="meetingPassword">
                Meeting Password
                <div class="text-sm font-sans-book">
                  If you have enabled a password for your event, it is critical that you enter it here. We recommend not
                  using a password in order to make it simpler for attendees.
                </div>
              </label>
              <input
                id="meetingPassword" v-model="form.venuePassword" name="meetingPassword"
                type="text">
            </div>
          </div>
          <div v-if="form.type == 'physical'" key="physical-event">
            <div class="mb-12">
              <label for="venueName">
                Location Name<span class="text-red">*</span>
              </label>
              <input
                id="venueName" key="physical-venue-name" v-model="form.venueName"
                v-validate="'required'"
                data-vv-validate-on="change"
                name="venueName"
                type="text"
              >
              <span v-show="errors.has('venueName')" class="text-lg error text-error-red">{{
                  errors.first('venueName')
                }}</span>
            </div>

            <div class="mb-12">
              <label for="venueAddress1">
                Venue Address<span class="text-red">*</span>
              </label>
              <input
                id="venueAddress1" key="physical-venue-address" v-model="form.venueAddress1"
                v-validate="'required'"
                data-vv-validate-on="change"
                name="venueAddress1"
                type="text"
              >
              <span v-show="errors.has('venueAddress1')"
                    class="text-lg error text-error-red">{{ errors.first('venueAddress1') }}</span>
            </div>

            <div class="flex mb-12">
              <div class="w-1/2 mr-2">
                <label for="city">
                  City
                </label>
                <input
                  id="city" v-model="form.venueCity" name="city"
                  type="text">
              </div>

              <div class="w-1/2 mr-2">
                <label for="state">
                  State
                </label>
                <input
                  id="state" v-model="form.venueState" name="state"
                  type="text">
              </div>
            </div>

            <div class="mb-12">
              <div class="w-1/2 mr-2">
                <label for="zip">
                  Zip
                </label>
                <input id="zip" v-model="form.venueZip" name="zip" type="text">
              </div>
            </div>

            <div class="mb-12">
              <label for="country">
                Country
              </label>
              <countries id="country" v-model="form.country" />
            </div>
          </div>


          <div class="mb-12">
            <label for="additionalInformation">
              Additional Instructions
              <div v-if="form.type === 'digital'" class="text-sm font-sans-book">
                Please enter dial-in details or any other important information.
              </div>
            </label>
            <textarea id="additionalInformation" v-model="form.additionalInfo" name="additionalInformation" type="text" />
          </div>

          <div class="mb-12">
            <label for="hostEmail">
              Host Email Contact for Attendees <span class="text-red">*</span>
              <div class="text-sm font-sans-book">Please enter an e-mail address so that attendees can contact you or an
                assistant in case there is an issue accessing your event.
              </div>
            </label>
            <input id="hostEmail" v-model="form.hostEmail" v-validate="'required|email'" data-vv-validate-on="change" name="hostEmail" type="text" />
            <span v-show="errors.has('hostEmail')" class="text-lg error text-error-red">
              {{errors.first('hostEmail') }}
            </span>
          </div>

          <div class="mb-12">
            <label for="hostPhone">
              Host Phone Contact for Attendees
            </label>
            <input id="hostPhone" v-model="form.hostPhone" name="hostPhone" type="text" />
            <span v-show="errors.has('hostPhone')" class="text-lg error text-error-red">
              {{errors.first('hostEmail')}}
            </span>
          </div>

          <div class="mb-6 border-top flex flex-wrap">
            <button
              :class="{ 'opacity-50 cursor-not-allowed': saving || !honorOfValid }"
              :disabled="saving || !honorOfValid"
              class="button"
              @click.stop.prevent="save">
                <template v-if="saving">Processing...</template>
                <template v-else>Save</template>
            </button>
            <div class="mr-0 md:mr-4 mb-4 md:mb-4 w-full md:w-auto" />
            <button
              :class="{ 'opacity-50 cursor-not-allowed': saving || !honorOfValid }"
              :disabled="saving || !honorOfValid"
              class="button"
              @click.stop.prevent="saveNext">
              Save & Next
            </button>
          </div>
        </form>
      </div>
      <div class="w-full pt-3 mt-10 border-black md:w-1/4 border-t-5 md:mt-2">
        <div class="mb-6">
          <p>PLEASE NOTE: If your event will be held on multiple days or you have multiple time sessions on the same day,
          you will need to create a separate event for each different day and/or time.</p>
        </div>

        <div class="w-full pt-3 mt-10 border-black border-t-5 md:mt-2" v-if="user.isAdmin">
          <h2 class="mb-4">Demographics</h2>
          <ul>
            <li v-for="option in userOptions">{{ option.name }}</li>
          </ul>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import eventSave from "../../../mixins/event-save";
import {VueEditor} from "vue2-editor";
import timeHelper from "../../../mixins/time-helper";
import messaging from "../../../mixins/messaging";
import TimePicker from "../../global/TimePicker.vue";
import urlFilter from "../../../mixins/url-filter";
import UrlInput from "../../global/UrlInput.vue";
import Countries from "../../global/Countries.vue";
import HonorOfManager from "./HonorOfManager.vue";
import LabDatePicker from "../../global/LabDatePicker.vue";
import RadioGroup from "../../global/RadioGroup.vue";

export default {
  name: "EventCreateBasics",
  title: "Create Event Basics",
  mixins: [eventSave, timeHelper, messaging, urlFilter],
  components: {
    RadioGroup,
    LabDatePicker,
    HonorOfManager,
    VueEditor,
    TimePicker,
    UrlInput,
    Countries
  },
  data: () => ({
    honorOfValid: true,
    form: {
      additionalInfo: "",
      country: {
        code: "US",
        name: "United States"
      },
      endTime: {},
      festival_id: "",
      hostEmail: "",
      hostPhone: "",
      loadEndTime: "",
      loadStartTime: "",
      private: false,
      startDate: "",
      startTime: {},
      title: "",
      type: "",
      venueAddress1: "",
      venueCity: "",
      venueId: "",
      venueName: "",
      venuePassword: "",
      venueState: "",
      venueUrl: "",
      venueZip: "",
      inHonorOfName: "",
      inHonorOfPhoto: "",
      inHonorOfDescription: "",
      isInHonorOf: false,
      hasHostedBefore: false,
      referredBy: '',
      openToPartnership: false,
      needMiniGrant: false,
      acceptDonations: true,
    },
    festivalTitle: "",
    festivalStartAt: "",
    festivalEndAt: "",
    submitted: false,
    saving: false,
    next: "events-details",
    customToolbar: [["link"]]
  }),
  watch: {
    event(event) {
      if (!_.isEmpty(event)) {
        this.form.additionalInfo = event.additionalInfo;
        this.form.festival_id = event.festival.id;
        this.form.hostEmail = event.hostEmail;
        this.form.hostPhone = event.hostPhone;
        this.form.private = event.private ? event.private : false;
        this.form.title = event.title;
        this.form.type = event.type ? event.type : "";
        this.form.inHonorOfName = event.inHonorOfName || "";
        this.form.inHonorOfPhoto = event.inHonorOfPhoto || "";
        this.form.inHonorOfDescription = event.inHonorOfDescription || "";
        this.form.isInHonorOf = event.isInHonorOf;
        this.form.hasHostedBefore = event.hasHostedBefore;
        this.form.referredBy = event.referredBy;
        this.form.openToPartnership = event.openToPartnership;
        this.form.needMiniGrant = event.needMiniGrant;
        this.form.acceptDonations = event.acceptDonations;

        if (event.start_at) {
          const startDateTime = event.start_at
            ? event.start_at.split(" ")
            : [];
          this.form.startDate = startDateTime[0];
          this.form.loadStartTime = this.formatTo12HourTime(
            startDateTime[1]
          );
        }

        if (event.end_at) {
          const endDateTime = event.end_at
            ? event.end_at.split(" ")
            : [];
          this.form.loadEndTime = this.formatTo12HourTime(
            endDateTime[1]
          );
        }

        this.festivalTitle = event.festival.title;
        this.festivalStartAt = event.festival.startAt;
        this.festivalEndAt = event.festival.endAt;

        if (event.venue) {
          this.form.venueId = event.venue.id;
          this.form.venueName = event.venue.name;
          this.form.venueUrl = this.filterUrl(event.venue.url);
          this.form.venuePassword = event.venue.password;

          this.form.venueAddress1 = event.venue.address_1;
          this.form.venueCity = event.venue.city;
          this.form.venueState = event.venue.state;
          this.form.venueZip = event.venue.zip;
          this.form.country = {
            code: event.venue.countryCode,
            name: event.venue.countryName
          };
        }

        this.$nextTick(function () {
          this.$store.dispatch("setUnsavedData", false);
        });
      }
    }
  },
  computed: {
    ...mapGetters({
      event: "event",
      validation: "validation",
      user: "user"
    }),
    days() {
      return this.event && this.event.festival
        ? _.map(this.event.festival.days, "day")
        : [];
    },
    characterCount() {
      return this.form.title ? this.form.title.length : 0;
    },
    titleValidate() {
      if(this.user.isAdmin) {
        return 'required|max:79';
      }

      return 'required|max:60';
    },
    userOptions() {
      return (this.event) ? this.event.user.options : []
    }
  },
  methods: {
    async save() {
      this.saving = true;

      const homValidatorResults = await this.$refs.honorOfManagerRef.$validator.validateAll();
      const validatorResults = await this.$validator.validateAll();

      if (!validatorResults || !homValidatorResults) {
        this.saving = false;
        return;
      }

      return this.$validator.validateAll().then(result => {
        if (!result) {
          this.saving = false;
          return;
        }

        this.$toasted.show("Event currently saving...");

        let route = this.$laroute.route("api.events.update", {
          event: this.event.id
        });

        this.form.startTime = this.formatTo24HourTime(
          this.form.loadStartTime
        );
        this.form.endTime = this.formatTo24HourTime(
          this.form.loadEndTime
        );

        return axios
          .put(route, this.form)
          .then(response => {
            this.showUpdatedToast();

            let eventResponse = response.data.data;
            this.$store.dispatch("setValidation", eventResponse);

            this.$store.dispatch("setUnsavedData", false);
            this.$store.dispatch("setEventHasErrors", false);

            this.saving = false;
          })
          .catch(error => {
            console.log(error);
            this.displayError(error);
          });
      });
    }
  },
  mounted() {
    this.$store.dispatch("getEvent", {id: this.$route.params.id});
    document.getElementById("dropdown").checked = false;
  }
};
</script>
